import './feature-article-cards.scss';
import React, { useRef, useState } from 'react';
import { ViewMoreTopics } from '../../../gvt/form-components/gvt-select-topic/view-more-topics';
import { DyfFeatureGuideContentProps } from '../../../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';
import { SingleFeatureArticleCard } from '../single-feature-article/single-feature-article-card';
import AstuteService from '../../../../../support/services/astute-service/astute-service';
import { UiFeature } from '../../../../../hooks/dyf/use-your-vehicle-features/vehicle-features.types';
import slugify from 'slugify';
import { useDyfQueryParams } from '../../../../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';

export interface FeatureArticleCardsProps {
    featureGuideContent: DyfFeatureGuideContentProps;
    featureArticles: UiFeature[];
    userGuid: string;
    currentlySelectedCategories: string[];
    languageRegionCode: string;
    flow: 'vin' | 'ymm';
    isAuthenticated: boolean;
}

export const FeatureArticleCards = (props: FeatureArticleCardsProps) => {
    const { featureArticles, languageRegionCode } = props;
    const { queryVin, queryModel, queryYear } = useDyfQueryParams();
    const getTitleHref = (selectedArticle: UiFeature) => {
        const astute = new AstuteService();
        const baseUrl =
            props.flow === 'vin'
                ? `/vehicle/vin/${queryVin}/discover-your-ford`
                : `/vehicle/${queryModel}/${queryYear}/discover-your-ford`;

        const categoryToAppend = () => {
            if (
                selectedArticle.categoryNames &&
                selectedArticle.categoryNames.length > 0
            ) {
                if (props.currentlySelectedCategories.length === 0) {
                    return `/${selectedArticle.categoryNames[0]}`;
                }

                if (props.currentlySelectedCategories.length === 1) {
                    return `/${props.currentlySelectedCategories[0]}`;
                }

                return `/${selectedArticle.categoryNames.find(category =>
                    props.currentlySelectedCategories.some(
                        selectedCategory => selectedCategory === category
                    )
                ) || selectedArticle.categoryNames[0]}`;
            }

            return '';
        };

        const categoryUrl = slugify(categoryToAppend(), {
            locale: props.languageRegionCode,
            lower: true,
        });

        const articleSlug = astute.sanitizeString(
            selectedArticle.title!,
            languageRegionCode
        );
        return `${baseUrl}/${categoryUrl}/${articleSlug}/overview/`;
    };

    const minimumArticles = useRef(7);
    const [visibleRangeEnd, setVisibleRangeEnd] = useState(
        minimumArticles.current
    );
    const calculateVisibleRangeEnd = () => {
        visibleRangeEnd + minimumArticles.current > featureArticles.length
            ? setVisibleRangeEnd(featureArticles.length)
            : setVisibleRangeEnd(visibleRangeEnd + minimumArticles.current);
        return visibleRangeEnd;
    };

    return (
        <div className="dyf-feature-article-content">
            <div className="card-grid">
                {featureArticles
                    .slice(0, visibleRangeEnd)
                    .filter(item => item.topicId)
                    .map(item => (
                        <SingleFeatureArticleCard
                            key={item.topicId}
                            item={item}
                            titleHref={getTitleHref(item)}
                            featureGuideContent={props.featureGuideContent}
                        />
                    ))}
            </div>
            <div className="articles-view-container">
                {featureArticles.length > minimumArticles.current &&
                    visibleRangeEnd < featureArticles.length && (
                        <>
                            <div className="articles-show-more">
                                <div
                                    className="articles-show-more-button"
                                    onClick={calculateVisibleRangeEnd}
                                >
                                    <ViewMoreTopics
                                        showMoreText={
                                            props.featureGuideContent
                                                ?.showMoreButton
                                        }
                                        isOpen={false}
                                        onClick={calculateVisibleRangeEnd}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                {featureArticles.length > minimumArticles.current && (
                    <div className="articles-pagination">
                        {props.featureGuideContent.paginationDisplayText} 1 -{' '}
                        {visibleRangeEnd > featureArticles.length
                            ? featureArticles.length
                            : visibleRangeEnd}{' '}
                        {props.featureGuideContent.paginationText || 'of'}{' '}
                        {featureArticles.length}
                    </div>
                )}
            </div>
        </div>
    );
};
